import { Link } from "react-router-dom";
import { Medal, Trophy } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

export interface LeaderboardEntry {
  rank: number;
  handle: string;
  pullCount: number;
  topTierName?: string | null;
}

export function LeaderboardTeaser({ entries }: { entries: LeaderboardEntry[] }) {
  if (!entries.length) return null;

  const medals = [Medal, Medal, Medal];
  const colors = ["text-amber-400", "text-white/70", "text-amber-700"];

  return (
    <section>
      <SectionHeader title="Top rippers" to="/leaderboard" actionLabel="Leaderboard" />
      <div className="glass-panel space-y-2 rounded-2xl p-3">
        {entries.slice(0, 3).map((entry, i) => {
          const Icon = medals[i] ?? Trophy;
          return (
            <div key={entry.rank} className="flex items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2">
              <Icon size={16} className={colors[i] ?? "text-white/50"} />
              <span className="w-6 text-center font-display text-xs font-bold text-white/40">#{entry.rank}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{entry.handle}</span>
              <span className="text-xs tabular-nums text-brand-pink">{entry.pullCount} pulls</span>
            </div>
          );
        })}
        <Link to="/leaderboard" className="block pt-1 text-center text-[11px] font-semibold text-brand-pink">
          View full leaderboard →
        </Link>
      </div>
    </section>
  );
}

export function LeaderboardList({ entries }: { entries: LeaderboardEntry[] }) {
  const medals = ["text-amber-400", "text-slate-300", "text-amber-700"];

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <div
          key={entry.rank}
          className={`flex items-center gap-3 rounded-2xl border border-white/5 bg-surface px-4 py-3 ${
            entry.rank <= 3 ? "neon-ring glow-brand" : ""
          }`}
        >
          <span
            className={`w-8 text-center font-display text-sm font-bold ${
              entry.rank <= 3 ? medals[entry.rank - 1] : "text-white/40"
            }`}
          >
            #{entry.rank}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{entry.handle}</p>
            {entry.topTierName ? (
              <p className="text-[10px] text-white/45">Best hit: {entry.topTierName}</p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="font-display text-lg font-bold tabular-nums text-brand-pink">{entry.pullCount}</p>
            <p className="text-[10px] text-white/40">pulls</p>
          </div>
        </div>
      ))}
    </div>
  );
}
